import { FormEvent, useEffect, useRef, useState } from "react";
import type { EventItem } from "../data";
import { createReferralLink, inspectReferralToken, submitAudience } from "../api";
import { formatEventDate, useLanguage } from "../i18n";
import { ArrowIcon } from "./ArrowIcon";
import { TurnstileWidget, type TurnstileHandle } from "./TurnstileWidget";

type EventModalProps = {
  event: EventItem | null;
  onClose: () => void;
};

export function EventModal({ event, onClose }: EventModalProps) {
  const { language, t } = useLanguage();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [message, setMessage] = useState("");
  const [discoverySource, setDiscoverySource] = useState("");
  const [referrerContact, setReferrerContact] = useState("");
  const [referralToken, setReferralToken] = useState("");
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [shareUrl, setShareUrl] = useState("");
  const [shareState, setShareState] = useState<"idle" | "preparing" | "ready" | "copied" | "error">("idle");
  const [manualShareVisible, setManualShareVisible] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileRef = useRef<TurnstileHandle>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!event) {
      if (dialog?.open) dialog.close();
      return;
    }

    setStatus("idle");
    setMessage("");
    setDiscoverySource("");
    setReferrerContact("");
    setReferralToken("");
    setRegisteredEmail("");
    setShareUrl("");
    setShareState("idle");
    setManualShareVisible(false);
    setTurnstileToken("");
    if (dialog && !dialog.open) dialog.showModal();

    const token = new URLSearchParams(window.location.search).get("ref");
    if (!token) return;
    let active = true;
    void inspectReferralToken(token).then((referral) => {
      if (!active || !referral || referral.eventSlug !== event.slug) return;
      setDiscoverySource("Friends");
      setReferrerContact(referral.maskedContact || t.registration.referredByLink);
      setReferralToken(token);
    });
    return () => { active = false; };
  }, [event, t.registration.referredByLink]);

  useEffect(() => {
    if (status !== "success") return;
    const frame = window.requestAnimationFrame(() => {
      dialogRef.current?.scrollTo({ top: 0, behavior: "smooth" });
      dialogRef.current?.querySelector<HTMLElement>(".dialog-content")?.scrollTo({ top: 0, behavior: "smooth" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [status]);

  if (!event) return null;
  const selectedEvent = event;

  async function preparePersonalLink(email: string) {
    setShareState("preparing");
    setManualShareVisible(false);
    try {
      const url = await createReferralLink(email, selectedEvent.slug);
      setShareUrl(url);
      setShareState("ready");
    } catch {
      setShareState("error");
    }
  }

  async function handleSubmit(submitEvent: FormEvent<HTMLFormElement>) {
    submitEvent.preventDefault();
    if (!turnstileToken) {
      setStatus("error");
      setMessage(t.form.turnstileRequired);
      return;
    }
    const form = submitEvent.currentTarget;
    const data = new FormData(form);
    const email = String(data.get("email") || "");
    setStatus("sending");
    setMessage("");
    try {
      await submitAudience({
        kind: "event",
        name: String(data.get("name") || ""),
        email,
        phone: String(data.get("phone") || ""),
        city: String(data.get("city") || ""),
        instagramId: String(data.get("instagramId") || ""),
        discoverySource,
        referrerContact: discoverySource === "Friends" && !referralToken ? referrerContact : undefined,
        referralToken: discoverySource === "Friends" ? referralToken : undefined,
        eventSlug: selectedEvent.slug,
        eventName: selectedEvent.title,
        eventDate: selectedEvent.date,
        guestCount: Number(data.get("additionalGuests") || 0) + 1,
        musicPreference: String(data.get("musicPreference") || ""),
        marketingOptIn: data.get("marketingOptIn") === "on",
        language,
        sourcePage: window.location.pathname + window.location.search,
        privacyVersion: "2026-08-15",
        turnstileToken,
      });
      setStatus("success");
      setMessage(t.registration.liveSuccess.replace("{email}", email));
      setRegisteredEmail(email);
      await preparePersonalLink(email);
    } catch (error) {
      setStatus("error");
      setMessage(error instanceof Error ? error.message : t.form.genericError);
    } finally {
      turnstileRef.current?.reset();
    }
  }

  async function sharePersonalLink() {
    if (!shareUrl) return;
    const shareData = { title: `${selectedEvent.title} — AXON`, text: selectedEvent.description[language], url: shareUrl };
    try {
      if (navigator.share && (!navigator.canShare || navigator.canShare(shareData))) {
        await navigator.share(shareData);
        return;
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
    }
    await copyPersonalLink();
  }

  async function copyPersonalLink() {
    if (!shareUrl) return;
    try {
      if (!navigator.clipboard?.writeText) throw new Error("Clipboard unavailable");
      await navigator.clipboard.writeText(shareUrl);
      setShareState("copied");
      setManualShareVisible(false);
    } catch {
      setManualShareVisible(true);
      setShareState("ready");
    }
  }

  return (
    <dialog className="event-dialog" ref={dialogRef} onClose={onClose} onCancel={onClose}>
      <button className="dialog-close" type="button" onClick={() => dialogRef.current?.close()} aria-label={t.registration.close}>×</button>
      <div className={`dialog-grid${status === "success" ? " dialog-grid--success" : ""}`}>
        <div className="dialog-poster" style={{ backgroundImage: `url(${event.flyer})` }} aria-hidden="true" />
        <div className={`dialog-content${status === "success" ? " dialog-content--success" : ""}`}>
          {status !== "success" && (
            <>
              <p className="eyebrow">{t.registration.eyebrow} · {formatEventDate(event.date, language)}</p>
              <h2>{event.title}</h2>
              <p className="dialog-lead">{t.registration.lead}</p>
            </>
          )}

          {status === "success" ? (
            <div className="registration-success">
              <div className="registration-email-confirmation" role="status">
                <p className="eyebrow">{language === "de" ? "E-MAIL BESTÄTIGEN" : "CONFIRM YOUR EMAIL"}</p>
                <h3>{message}</h3>
                <p>{t.registration.emailVerificationHint}</p>
              </div>
              <div className="registration-share">
                <p className="eyebrow">{t.registration.shareTitle}</p>
                <h3>{t.registration.shareTitle}</h3>
                <p>{t.registration.shareBody}</p>
                {shareState === "preparing" && (
                  <button className="button button--pink" type="button" disabled>
                    {t.registration.sharePreparing}
                  </button>
                )}
                {(shareState === "ready" || shareState === "copied") && (
                  <div className="registration-share-actions">
                    <button className="button button--pink" type="button" onClick={sharePersonalLink}>
                      {t.registration.shareCta}<ArrowIcon />
                    </button>
                    <button className="button button--outline-light" type="button" onClick={copyPersonalLink}>
                      {shareState === "copied" ? t.registration.shareCopied : t.registration.shareCopy}
                    </button>
                  </div>
                )}
                {shareState === "error" && (
                  <div className="registration-share-retry">
                    <p className="form-status form-status--error" role="status">{t.registration.shareError}</p>
                    <button className="button button--outline-light" type="button" onClick={() => void preparePersonalLink(registeredEmail)}>
                      {t.registration.shareRetry}
                    </button>
                  </div>
                )}
                {manualShareVisible && (
                  <label className="registration-share-manual">
                    <span>{t.registration.shareManual}</span>
                    <input value={shareUrl} readOnly onFocus={(focusEvent) => focusEvent.currentTarget.select()} onClick={(clickEvent) => clickEvent.currentTarget.select()} />
                  </label>
                )}
              </div>
            </div>
          ) : (
            <form className="axon-form axon-form--dialog" onSubmit={handleSubmit}>
              <div className="form-row">
                <label><span>{t.form.name}</span><input name="name" autoComplete="name" required placeholder={t.form.namePlaceholder} /></label>
                <label><span>{t.form.email}</span><input name="email" type="email" autoComplete="email" required placeholder="you@email.com" /></label>
              </div>
              <div className="form-row">
                <label><span>{t.registration.phone}</span><input name="phone" type="tel" autoComplete="tel" placeholder={t.registration.optional} /></label>
                <label><span>{t.form.city}</span><input name="city" autoComplete="address-level2" placeholder={t.form.cityPlaceholder} /></label>
              </div>
              <div className="form-row">
                <label>
                  <span>{t.registration.instagram}</span>
                  <input name="instagramId" autoComplete="off" placeholder={t.registration.instagramPlaceholder} />
                </label>
                <label>
                  <span>{t.form.sound}</span>
                  <select name="musicPreference" defaultValue="All">
                    <option value="House">{t.form.house}</option><option value="Afro House">{t.form.afro}</option><option value="Melodic Techno">{t.form.melodicTechno}</option><option value="All">{t.form.all}</option>
                  </select>
                </label>
              </div>
              {selectedEvent.registration.maxAdditionalGuests > 0 && (
                <label>
                  <span>{t.registration.additionalGuests}</span>
                  <select name="additionalGuests" defaultValue="0" aria-describedby="guest-count-hint">
                    {Array.from({ length: selectedEvent.registration.maxAdditionalGuests + 1 }, (_, count) => (
                      <option key={count} value={count}>{count}</option>
                    ))}
                  </select>
                  <small id="guest-count-hint" className="field-hint">{t.registration.additionalGuestsHint}</small>
                </label>
              )}
              <div className="form-row form-row--conditional">
                <label>
                  <span>{t.registration.discovery}</span>
                  <select name="discoverySource" value={discoverySource} onChange={(changeEvent) => {
                    const nextSource = changeEvent.target.value;
                    setDiscoverySource(nextSource);
                    if (nextSource !== "Friends") {
                      setReferrerContact("");
                      setReferralToken("");
                    }
                  }}>
                    <option value="">{t.registration.discoveryPlaceholder}</option>
                    <option value="Instagram">{t.registration.discoveryInstagram}</option>
                    <option value="Google">{t.registration.discoveryGoogle}</option>
                    <option value="Friends">{t.registration.discoveryFriends}</option>
                  </select>
                </label>
                {discoverySource === "Friends" && (
                  <label>
                    <span>{t.registration.referrerContact}</span>
                    <input name="referrerContact" value={referrerContact} readOnly={Boolean(referralToken)} onChange={(changeEvent) => setReferrerContact(changeEvent.target.value)} placeholder={t.registration.referrerContactPlaceholder} />
                  </label>
                )}
              </div>
              <label className="check-label"><input name="marketingOptIn" type="checkbox" /><span>{t.registration.updates}</span></label>
              <p className="registration-admission">{t.registration.admissionNotice}</p>
              <p className="registration-disclaimer">{t.registration.disclaimer}</p>
              <p className="form-privacy-note form-privacy-note--dialog">
                {language === "de" ? <>Mit dem Absenden bestätigst du, dass du unsere <a href="/datenschutz" target="_blank" rel="noreferrer">Datenschutzerklärung</a> zur Kenntnis genommen hast. Wir verarbeiten deine Kontaktdaten zur Bearbeitung deiner Event-Registrierung.</> : <>By submitting, you confirm that you have read our <a href="/datenschutz" target="_blank" rel="noreferrer">privacy policy</a>. We process your contact details to handle your event registration.</>}
              </p>
              <TurnstileWidget
                ref={turnstileRef}
                action="event_register"
                onToken={setTurnstileToken}
                onError={() => {
                  setStatus("error");
                  setMessage(t.form.turnstileError);
                }}
              />
              <button className="button button--dark" disabled={status === "sending" || !turnstileToken} type="submit">
                {status === "sending" ? t.registration.submitting : t.registration.submit}<ArrowIcon />
              </button>
              {message && <p className={`form-status form-status--${status}`} role="status">{message}</p>}
            </form>
          )}
        </div>
      </div>
    </dialog>
  );
}
